import getCustomerApi from '@/api/get/customer/get-customer';
import updateCustomerApi from '@/api/patch/customer/update-customer';
import SubmitCard from '@/components/card/submit-card-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation } from '@/hooks/use-api';
import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Alert from '@/components/alert/alert-component';
import CustomerForm from './customer-form';

/**
 *
 * @returns {JSX.Element} The rendered Customer Edit page component.
 */
const CustomerEdit = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [updateCustomer, { loading }] = useMutation(updateCustomerApi);
  const [initialContactPerson, setInitialContactPerson] = useState([]);
  const [initialOperatingSite, setInitialOperatingSite] = useState([]);

  const objectsAreEqual = (obj1, obj2) => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) {
      return false;
    }
    return keys1.every((key) => obj1[key] === obj2[key]);
  };

  const fromQuote = !!state?.quoteForm;

  return (
    <FormWrapper
      defaultValues={async () => {
        const customer = await getCustomerApi(customerId);
        let renderContactPerson;

        if (fromQuote) {
          customer?.data?.contactPerson?.push({}); // This will add a new contact person to the form for quote form
          renderContactPerson = customer?.data?.contactPerson;
        } else {
          renderContactPerson = customer?.data?.contactPerson;
        }

        setInitialOperatingSite(customer.data.operatingSite);
        setInitialContactPerson(renderContactPerson);
        return {
          ...customer.data.customer,
          contact_person: customer.data.contactPerson,
          operating_site: customer.data.operatingSite,
        };
      }}
      onSubmit={(sValues) => {
        const values = { ...sValues };
        const contactPersons = values.contact_person.map((cP) => cP);
        const operatingSites = values.operating_site.map((oS) => oS);

        const addContactPersons = contactPersons
          .filter(
            (cP) =>
              !initialContactPerson.some((icP) => icP.id === cP.id) || !cP.id,
          )
          .map((oS) => {
            const { id, ...s } = oS;
            const isSuperior = contactPersons.some((cP) => cP.superior === id);
            if (isSuperior) {
              return oS;
            }
            return s;
          });

        const removeContactPersons = initialContactPerson
          .filter((icP) => !contactPersons.some((cP) => cP.id === icP.id))
          .map((icP) => icP.id);

        const updateContactPersons = contactPersons
          .filter(
            (cP) =>
              initialContactPerson.some(
                (icP) => icP.id === cP.id && !objectsAreEqual(icP, cP),
              ) && cP.id,
          )
          .map((cP) => ({ id: cP.id, ...cP }));

        const addOperatingSites = operatingSites
          .filter((oS) => !initialOperatingSite.some((iS) => iS.id === oS.id))
          .map((oS) => {
            const { id, ...s } = oS;
            return s;
          });

        const removeOperatingSites = initialOperatingSite
          .filter((iS) => !operatingSites.some((oS) => oS.id === iS.id))
          .map((iS) => iS.id);

        const updateOperatingSites = operatingSites
          .filter((oS) =>
            initialOperatingSite.some(
              (iS) => iS.id === oS.id && !objectsAreEqual(iS, oS),
            ),
          )
          .map((oS) => ({ id: oS.id, ...oS }));

        delete values.id;
        delete values.contact_person;
        delete values.operating_site;

        const submitValues = {
          id: customerId,
          customer: {
            ...values,
          },
          contactPerson: {
            add: addContactPersons,
            remove: removeContactPersons,
            update: updateContactPersons,
          },
          operatingSite: {
            add: addOperatingSites,
            remove: removeOperatingSites,
            update: updateOperatingSites,
          },
        };

        updateCustomer(customerId, submitValues).then(({ data }) => {
          if (state?.quoteForm) {
            const { formData, tab } = state;
            formData.company_id = data?.id;
            navigate(`/shop/sales/main-summary/${formData.quote.id}/quote`, {
              state: { formData, tab, quoteId: formData.quote.id },
            });
          } else {
            navigate(`/shop/customer-management`);
          }
          Alert({
            icon: 'success',
            text: 'Customer created successfully',
          });
        });
      }}
    >
      <CustomerForm fromQuote={fromQuote} />
      <SubmitCard
        padding="0"
        primaryButtonTitle="Save"
        primaryButtonType="submit"
        primaryButtonDisabled={loading}
        primaryButtonWidth="112px"
        secondaryButtonType="button"
        secondaryButtonDisabled={loading}
        secondaryButtonWidth="112px"
        isLoading={loading}
        secondaryButtonTitle="Delete"
      />
    </FormWrapper>
  );
};

export default CustomerEdit;
